import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {pinCommentMutation} from './__generated__/pinCommentMutation.graphql'

export function pinCommentMutation({
  environment,
  input: {id},
  viewerLogin,
  issueId,
  onCompleted,
  onError,
}: {
  environment: Environment
  input: {id: string}
  viewerLogin?: string
  issueId: string
  onCompleted?: () => void
  onError?: (error?: Error) => void
}) {
  return commitMutation<pinCommentMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation pinCommentMutation($input: PinIssueCommentInput!) @raw_response_type {
        pinIssueComment(input: $input) {
          clientMutationId
          issueComment {
            id
            isPinned
            issue {
              id
              pinnedIssueComment {
                pinnedBy {
                  login
                }
                issueComment {
                  id
                  shortBodyHTML(limit: 700, stripBlockElements: false)
                  createdAt
                  url
                  viewerCanUnpin
                  author {
                    login
                    avatarUrl
                    profileUrl
                  }
                }
              }
            }
          }
        }
      }
    `,
    variables: {
      input: {issueCommentId: id},
    },
    optimisticUpdater: store => {
      const issueRecord = store.get(issueId)
      const commentRecord = store.get(id)

      if (issueRecord && commentRecord) {
        commentRecord.setValue(true, 'isPinned')

        // Create the PinnedIssueComment join record and link the comment
        const pinnedCommentKey = `client:pinnedIssueComment:${issueId}`
        const pinnedCommentRecord = store.create(pinnedCommentKey, 'PinnedIssueComment')
        pinnedCommentRecord.setLinkedRecord(commentRecord, 'issueComment')

        // Create the pinnedBy user and link to both the PinnedIssueComment
        // (for PinnedCommentDisplay) and the IssueComment (for the header badge)
        if (viewerLogin) {
          const pinnedByKey = `client:pinnedBy:${id}`
          const pinnedByRecord = store.create(pinnedByKey, 'User')
          pinnedByRecord.setValue(viewerLogin, 'login')
          pinnedCommentRecord.setLinkedRecord(pinnedByRecord, 'pinnedBy')
          commentRecord.setLinkedRecord(pinnedByRecord, 'pinnedBy')
        }

        // Link the PinnedIssueComment to the Issue so PinnedCommentDisplay renders
        issueRecord.setLinkedRecord(pinnedCommentRecord, 'pinnedIssueComment')

        // Ensure the comment has shortBodyHTML for the pinned display.
        // Fall back to bodyHTML if shortBodyHTML is not already available.
        const shortBodyStorageKey = 'shortBodyHTML(limit:700,stripBlockElements:false)'
        const existingShortBody = commentRecord.getValue(shortBodyStorageKey)
        if (existingShortBody == null) {
          const bodyHTML =
            commentRecord.getValue('bodyHTML(unfurlReferences:true)') ?? commentRecord.getValue('bodyHTML')
          if (typeof bodyHTML === 'string') {
            commentRecord.setValue(bodyHTML, shortBodyStorageKey)
          }
        }

        commentRecord.setValue(true, 'viewerCanUnpin')
      }
    },
    onCompleted: response => {
      if (response.pinIssueComment === null) {
        onError?.()
      } else {
        onCompleted?.()
      }
    },
    onError: error => onError?.(error),
  })
}
